import { i18next } from '@frontend/i18n';
import { TableColumnConfig } from '@frontend/design-system';
import { CellRenderer, ServiceDatesRenderer } from '../components';
import {
  CoinsuranceTableDataType,
  CopaymentTableDataType,
  DeductibleTableDataType,
  LimitationTableDataType,
  ServiceHistory,
} from '../types';

export const coInsuranceTableDefaultColumnKeys: (keyof CoinsuranceTableDataType)[] = [
  'network',
  'category',
  'planPeriod',
  'serviceType',
  'insuranceType',
  'coverageLevel',
  'percent',
  'message',
  'disclaimers',
  'authorizationRequired',
];

export const getCoinsurancesTableColConfigs = (
  tableColumnKeys: (keyof CoinsuranceTableDataType)[] = coInsuranceTableDefaultColumnKeys
): TableColumnConfig<CoinsuranceTableDataType>[] => [
  ...(tableColumnKeys.includes('network')
    ? [
        {
          Header: () => <>{i18next.t('Network', { ns: 'insuranceEligibilityReport' })}</>,
          headerLabel: i18next.t('Network', { ns: 'insuranceEligibilityReport' }),
          accessor: 'network' as const,
          cellRenderer: (data: string) => <CellRenderer type='network'>{data}</CellRenderer>,
          id: 'network',
          disableSortBy: true,
          width: 120,
        },
      ]
    : []),
  ...(tableColumnKeys.includes('category')
    ? [
        {
          Header: () => <>{i18next.t('Category', { ns: 'insuranceEligibilityReport' })}</>,
          accessor: 'category' as const,
          cellRenderer: (data: string) => <CellRenderer type='category'>{data}</CellRenderer>,
          id: 'category',
          disableSortBy: true,
        },
      ]
    : []),
  ...(tableColumnKeys.includes('serviceType')
    ? [
        {
          Header: () => <>{i18next.t('Service Type', { ns: 'insuranceEligibilityReport' })}</>,
          accessor: 'serviceType' as const,
          cellRenderer: (data: string) => <CellRenderer type='serviceType'>{data}</CellRenderer>,
          id: 'serviceType',
          disableSortBy: true,
        },
      ]
    : []),
  ...(tableColumnKeys.includes('planPeriod')
    ? [
        {
          Header: () => <>{i18next.t('Plan Period', { ns: 'insuranceEligibilityReport' })}</>,
          accessor: 'planPeriod' as const,
          cellRenderer: (data: string) => <CellRenderer type='planPeriod'>{data}</CellRenderer>,
          id: 'planPeriod',
          disableSortBy: true,
        },
      ]
    : []),
  ...(tableColumnKeys.includes('insuranceType')
    ? [
        {
          Header: () => <>{i18next.t('Insurance Type', { ns: 'insuranceEligibilityReport' })}</>,
          accessor: 'insuranceType' as const,
          cellRenderer: (data: string) => <CellRenderer type='insuranceType'>{data}</CellRenderer>,
          id: 'insuranceType',
          disableSortBy: true,
        },
      ]
    : []),
  ...(tableColumnKeys.includes('coverageLevel')
    ? [
        {
          Header: () => <>{i18next.t('Coverage Level', { ns: 'insuranceEligibilityReport' })}</>,
          accessor: 'coverageLevel' as const,
          cellRenderer: (data: string) => <CellRenderer type='coverageLevel'>{data}</CellRenderer>,
          id: 'coverageLevel',
          disableSortBy: true,
        },
      ]
    : []),
  ...(tableColumnKeys.includes('percent')
    ? [
        {
          Header: () => <>{i18next.t('Percent', { ns: 'insuranceEligibilityReport' })}</>,
          accessor: 'percent' as const,
          cellRenderer: (data: string) => <CellRenderer type='percent'>{data}</CellRenderer>,
          id: 'percent',
          disableSortBy: true,
        },
      ]
    : []),
  ...(tableColumnKeys.includes('message')
    ? [
        {
          Header: () => <>{i18next.t('Message', { ns: 'insuranceEligibilityReport' })}</>,
          accessor: 'message' as const,
          cellRenderer: (data: string) => <CellRenderer type='message'>{data}</CellRenderer>,
          id: 'message',
          disableSortBy: true,
          width: 160,
        },
      ]
    : []),
  ...(tableColumnKeys.includes('disclaimers')
    ? [
        {
          Header: () => <>{i18next.t('Disclaimers', { ns: 'insuranceEligibilityReport' })}</>,
          accessor: 'disclaimers' as const,
          cellRenderer: (data: string) => <CellRenderer type='disclaimers'>{data}</CellRenderer>,
          id: 'disclaimers',
          disableSortBy: true,
          width: 120,
        },
      ]
    : []),
  ...(tableColumnKeys.includes('authorizationRequired')
    ? [
        {
          Header: () => <>{i18next.t('Authorization Required', { ns: 'insuranceEligibilityReport' })}</>,
          accessor: 'authorizationRequired' as const,
          cellRenderer: (data: string) => <CellRenderer type='authorizationRequired'>{data}</CellRenderer>,
          id: 'authorizationRequired',
          disableSortBy: true,
          width: 120,
        },
      ]
    : []),
];

export const copaymentsTableColConfigs: TableColumnConfig<CopaymentTableDataType>[] = [
  {
    Header: () => <>{i18next.t('Network', { ns: 'insuranceEligibilityReport' })}</>,
    accessor: 'network',
    cellRenderer: (data: string) => <CellRenderer type='network'>{data}</CellRenderer>,
    id: 'network',
    disableSortBy: true,
    width: 100,
  },
  {
    Header: () => <>{i18next.t('Category', { ns: 'insuranceEligibilityReport' })}</>,
    accessor: 'category',
    cellRenderer: (data: string) => <CellRenderer type='category'>{data}</CellRenderer>,
    id: 'category',
    disableSortBy: true,
  },
  {
    Header: () => <>{i18next.t('Amount', { ns: 'insuranceEligibilityReport' })}</>,
    accessor: 'amount',
    cellRenderer: (data: string) => <CellRenderer type='amount'>{data}</CellRenderer>,
    id: 'amount',
    disableSortBy: true,
  },
  {
    Header: () => <>{i18next.t('Coverage Level', { ns: 'insuranceEligibilityReport' })}</>,
    accessor: 'coverageLevel',
    cellRenderer: (data: string) => <CellRenderer type='coverageLevel'>{data}</CellRenderer>,
    id: 'coverageLevel',
    disableSortBy: true,
  },
  {
    Header: () => <>{i18next.t('Disclaimers', { ns: 'insuranceEligibilityReport' })}</>,
    accessor: 'disclaimers',
    cellRenderer: (data: string) => <CellRenderer type='disclaimers'>{data}</CellRenderer>,
    id: 'disclaimers',
    disableSortBy: true,
    width: 120,
  },
];

export const getDeductiblesTableColConfigs = (
  isPrintingReport = false
): TableColumnConfig<DeductibleTableDataType>[] => {
  return [
    {
      Header: () => <>{i18next.t('Network', { ns: 'insuranceEligibilityReport' })}</>,
      accessor: 'network',
      cellRenderer: (data: string) => <CellRenderer type='network'>{data}</CellRenderer>,
      id: 'network',
      disableSortBy: true,
      width: isPrintingReport ? 50 : 120,
    },
    {
      Header: () => <>{i18next.t('Category', { ns: 'insuranceEligibilityReport' })}</>,
      accessor: 'category',
      cellRenderer: (data: string) => <CellRenderer type='category'>{data}</CellRenderer>,
      id: 'category',
      disableSortBy: true,
      ...(isPrintingReport && { width: 50 }),
    },
    {
      Header: () => <>{i18next.t('Coverage Level', { ns: 'insuranceEligibilityReport' })}</>,
      accessor: 'coverageLevel',
      cellRenderer: (data: string) => <CellRenderer type='coverageLevel'>{data}</CellRenderer>,
      id: 'coverageLevel',
      disableSortBy: true,
      width: isPrintingReport ? 100 : 152,
    },
    {
      Header: () => <>{i18next.t('Insurance Type', { ns: 'insuranceEligibilityReport' })}</>,
      accessor: 'insuranceType',
      cellRenderer: (data: string) => <CellRenderer type='insuranceType'>{data}</CellRenderer>,
      id: 'insuranceType',
      disableSortBy: true,
      width: isPrintingReport ? 100 : 152,
    },
    {
      Header: () => <>{i18next.t('Message', { ns: 'insuranceEligibilityReport' })}</>,
      accessor: 'message',
      cellRenderer: (data: string) => <CellRenderer type='message'>{data}</CellRenderer>,
      id: 'message',
      disableSortBy: true,
      width: isPrintingReport ? 100 : 152,
    },
    {
      Header: () => <>{i18next.t('Total Per Calender Year', { ns: 'insuranceEligibilityReport' })}</>,
      accessor: 'calendarAmount',
      cellRenderer: (data: string) => <CellRenderer type='calendarAmount'>{data}</CellRenderer>,
      id: 'calendarAmount',
      disableSortBy: true,
      ...(isPrintingReport && { width: 50 }),
    },
    {
      Header: () => <>{i18next.t('Remaining This Year', { ns: 'insuranceEligibilityReport' })}</>,
      accessor: 'remainingAmount',
      cellRenderer: (data: string) => <CellRenderer type='remainingAmount'>{data}</CellRenderer>,
      id: 'remainingAmount',
      disableSortBy: true,
      ...(isPrintingReport && { width: 50 }),
    },
    {
      Header: () => <>{i18next.t('Disclaimers', { ns: 'insuranceEligibilityReport' })}</>,
      accessor: 'disclaimers',
      cellRenderer: (data: string) => <CellRenderer type='disclaimers'>{data}</CellRenderer>,
      id: 'disclaimers',
      disableSortBy: true,
      width: isPrintingReport ? 75 : 152,
    },
  ];
};

export const limitationsTableDefaultColumnKeys: (keyof LimitationTableDataType)[] = [
  'coverage',
  'coverageCategory',
  'remaining',
  'serviceDates',
  'planPeriod',
  'insuranceType',
  'procedureCode',
  'message',
  'ageLimit',
  'limitation',
  'disclaimers',
  'limitationAppliesTo',
];

export const getLimitationsTableColConfigs = (
  isPrintingReport = false,
  limitationsTableColumnKeys = limitationsTableDefaultColumnKeys
): TableColumnConfig<LimitationTableDataType>[] => {
  return [
    ...(limitationsTableColumnKeys?.includes('coverage')
      ? [
          {
            Header: () => <>{i18next.t('Coverage', { ns: 'insuranceEligibilityReport' })}</>,
            headerExportValue: () => i18next.t('Coverage', { ns: 'insuranceEligibilityReport' }),
            accessor: 'coverage' as const,
            cellRenderer: (data: string) => <CellRenderer type='coverage'>{data}</CellRenderer>,
            id: 'coverage',
            disableSortBy: true,
            ...(isPrintingReport && { width: 10 }),
          },
        ]
      : []),
    ...(limitationsTableColumnKeys?.includes('coverageCategory')
      ? [
          {
            Header: () => <>{i18next.t('Coverage Category', { ns: 'insuranceEligibilityReport' })}</>,
            headerExportValue: () => i18next.t('Coverage Category', { ns: 'insuranceEligibilityReport' }),
            accessor: 'coverageCategory' as const,
            cellRenderer: (data: string) => <CellRenderer type='coverageCategory'>{data}</CellRenderer>,
            id: 'coverageCategory',
            disableSortBy: true,
            ...(isPrintingReport && { width: 10 }),
          },
        ]
      : []),
    ...(limitationsTableColumnKeys?.includes('remaining')
      ? [
          {
            Header: () => <>{i18next.t('Remaining', { ns: 'insuranceEligibilityReport' })}</>,
            headerExportValue: () => i18next.t('Remaining', { ns: 'insuranceEligibilityReport' }),
            accessor: 'remaining' as const,
            cellRenderer: (data: string) => <CellRenderer type='remaining'>{data}</CellRenderer>,
            id: 'remaining',
            disableSortBy: true,
            ...(isPrintingReport && { width: 10 }),
          },
        ]
      : []),
    ...(limitationsTableColumnKeys?.includes('serviceDates')
      ? [
          {
            Header: () => <>{i18next.t('Service Dates', { ns: 'insuranceEligibilityReport' })}</>,
            id: 'serviceDates',
            accessor: 'serviceDates' as const,
            cellRenderer: (serviceDates: ServiceHistory[]) =>
              !!serviceDates.length ? <ServiceDatesRenderer serviceDates={serviceDates} /> : '-',
            headerExportValue: () => i18next.t('Service Dates', { ns: 'insuranceEligibilityReport' }),
            disableSortBy: true,
            ...(isPrintingReport && { width: 130 }),
          },
        ]
      : []),
    ...(limitationsTableColumnKeys?.includes('planPeriod')
      ? [
          {
            Header: () => <>{i18next.t('Plan Period', { ns: 'insuranceEligibilityReport' })}</>,
            headerExportValue: () => i18next.t('Plan Period', { ns: 'insuranceEligibilityReport' }),
            accessor: 'planPeriod' as const,
            cellRenderer: (data: string) => <CellRenderer type='planPeriod'>{data}</CellRenderer>,
            id: 'planPeriod',
            disableSortBy: true,
            ...(isPrintingReport && { width: 50 }),
          },
        ]
      : []),
    ...(limitationsTableColumnKeys?.includes('coverageLevel')
      ? [
          {
            Header: () => <>{i18next.t('Coverage Level', { ns: 'insuranceEligibilityReport' })}</>,
            headerExportValue: () => i18next.t('Coverage Level', { ns: 'insuranceEligibilityReport' }),
            accessor: 'coverageLevel' as const,
            cellRenderer: (data: string) => <CellRenderer type='coverageLevel'>{data}</CellRenderer>,
            id: 'coverageLevel',
            disableSortBy: true,
            ...(isPrintingReport && { width: 10 }),
          },
        ]
      : []),
    ...(limitationsTableColumnKeys?.includes('insuranceType')
      ? [
          {
            Header: () => <>{i18next.t('Insurance Type', { ns: 'insuranceEligibilityReport' })}</>,
            headerExportValue: () => i18next.t('Insurance Type', { ns: 'insuranceEligibilityReport' }),
            accessor: 'insuranceType' as const,
            cellRenderer: (data: string) => <CellRenderer type='insuranceType'>{data}</CellRenderer>,
            id: 'insuranceType',
            disableSortBy: true,
            ...(isPrintingReport && { width: 10 }),
          },
        ]
      : []),
    ...(limitationsTableColumnKeys?.includes('procedureCode')
      ? [
          {
            Header: () => <>{i18next.t('Procedure Code', { ns: 'insuranceEligibilityReport' })}</>,
            headerExportValue: () => i18next.t('Procedure Code', { ns: 'insuranceEligibilityReport' }),
            accessor: 'procedureCode' as const,
            cellRenderer: (data: string) => <CellRenderer type='procedureCode'>{data}</CellRenderer>,
            id: 'procedureCode',
            disableSortBy: true,
            ...(isPrintingReport && { width: 10 }),
          },
        ]
      : []),
    ...(limitationsTableColumnKeys?.includes('message')
      ? [
          {
            Header: () => <>{i18next.t('Message', { ns: 'insuranceEligibilityReport' })}</>,
            headerExportValue: () => i18next.t('Message', { ns: 'insuranceEligibilityReport' }),
            accessor: 'message' as const,
            cellRenderer: (data: string) => <CellRenderer type='message'>{data}</CellRenderer>,
            id: 'message',
            disableSortBy: true,
            ...(isPrintingReport && { width: 10 }),
          },
        ]
      : []),
    ...(limitationsTableColumnKeys?.includes('ageLimit')
      ? [
          {
            Header: () => <>{i18next.t('Age Limit', { ns: 'insuranceEligibilityReport' })}</>,
            headerExportValue: () => i18next.t('Age Limit', { ns: 'insuranceEligibilityReport' }),
            accessor: 'ageLimit' as const,
            cellRenderer: (data: string) => <CellRenderer type='ageLimit'>{data}</CellRenderer>,
            id: 'ageLimit',
            disableSortBy: true,
            ...(isPrintingReport && { width: 10 }),
          },
        ]
      : []),

    ...(limitationsTableColumnKeys?.includes('limitation')
      ? [
          {
            Header: () => <>{i18next.t('Limitation', { ns: 'insuranceEligibilityReport' })}</>,
            headerExportValue: () => i18next.t('Limitation', { ns: 'insuranceEligibilityReport' }),
            accessor: 'limitation' as const,
            cellRenderer: (data: string) => <CellRenderer type='limitation'>{data}</CellRenderer>,
            id: 'limitation',
            disableSortBy: true,
            ...(isPrintingReport && { width: 70 }),
          },
        ]
      : []),
    ...(limitationsTableColumnKeys?.includes('disclaimers')
      ? [
          {
            Header: () => <>{i18next.t('Disclaimers', { ns: 'insuranceEligibilityReport' })}</>,
            headerExportValue: () => i18next.t('Disclaimers', { ns: 'insuranceEligibilityReport' }),
            accessor: 'disclaimers' as const,
            cellRenderer: (data: string) => <CellRenderer type='disclaimers'>{data}</CellRenderer>,
            id: 'disclaimers',
            disableSortBy: true,
            ...(isPrintingReport && { width: 120 }),
          },
        ]
      : []),
    ...(limitationsTableColumnKeys?.includes('limitationAppliesTo')
      ? [
          {
            Header: () => <>{i18next.t('Limitation Applies To', { ns: 'insuranceEligibilityReport' })}</>,
            headerExportValue: () => i18next.t('Limitation Applies To', { ns: 'insuranceEligibilityReport' }),
            accessor: 'limitationAppliesTo' as const,
            cellRenderer: (data: string) => <CellRenderer type='limitationAppliesTo'>{data}</CellRenderer>,
            id: 'limitationAppliesTo',
            disableSortBy: true,
            ...(isPrintingReport && { width: 80 }),
          },
        ]
      : []),
  ];
};
